import { formatHoursToHM } from '@/composable/useFormatDateAndHours'
import { useReportStore } from '@/store/report'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export function useUtilizationAmountHoursChart(sensorData: any, sensorCompareData: any) {
  const { t } = useI18n()
  const reportStore = useReportStore()

  const seriesHour = ref([
    {
      name: 'Total Base',
      data: [sensorData.totalUtilizationHours],
    },
    {
      name: 'Meta',
      data: [sensorData.metaHours],
    },
    {
      name: 'Total Comparação',
      data: [sensorCompareData.totalUtilizationHours],
    },
  ])

  const chartOptionsHour = ref({
    series: seriesHour.value,
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
        tools: {
          download: false,
        },
      },
    },
    colors: [],
    plotOptions: {
      bar: {
        horizontal: false,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => formatHoursToHM(val),
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
      background: {
        enabled: true,
        foreColor: '#000',
        padding: 6,
        borderColor: '#000',
        borderWidth: 1,
        opacity: 1,
      },
    },
    stroke: {
      show: true,
      width: 1,
      colors: ['#fff'],
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'regular',
      labels: {
        colors: ['#000'],
        useSeriesColors: true,
      },
      position: 'bottom',
      offsetY: 10,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => formatHoursToHM(val),
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => formatHoursToHM(val),
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
    xaxis: {
      categories: [''],
      labels: {
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
  })

  watch(
    () => [reportStore.getBaseChartColors, reportStore.getCompareChartColors],
    ([baseColor, compareColor]) => {
      chartOptionsHour.value = {
        ...chartOptionsHour.value,
        colors: [baseColor, '#353a45', compareColor],
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getAmountEnergySensorUtilizationHours,
    (newData) => {
      if (newData) {
        seriesHour.value[0].data = [newData.totalUtilizationHours]
        seriesHour.value[1].data = [newData.metaHours]
        seriesHour.value[2].data = [sensorCompareData.totalUtilizationHours]
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getCompareAmountEnergySensorUtilizationHours,
    (newData) => {
      if (newData) {
        seriesHour.value[0].data = [sensorData.totalUtilizationHours]
        seriesHour.value[1].data = [sensorData.metaHours]
        seriesHour.value[2].data = [newData.totalUtilizationHours]
      } else {
        seriesHour.value[2].data = []
      }
    },
    { immediate: true, deep: true },
  )

  return { chartOptionsHour, seriesHour }
}

export function useUtilizationAmountPercentageChart(sensorData: any, sensorCompareData: any) {
  const { t } = useI18n()
  const reportStore = useReportStore()

  const seriesPercentage = ref([
    {
      name: 'Total Base',
      data: [sensorData.totalUtilizationPercentage],
    },
    {
      name: 'Meta',
      data: [sensorData.metaPercentage],
    },
    {
      name: 'Total Comparação',
      data: [sensorCompareData.totalUtilizationPercentage],
    },
  ])

  const chartOptionsPercent = ref({
    series: seriesPercentage.value,
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
        tools: {
          download: false,
        },
      },
    },
    colors: [],
    plotOptions: {
      bar: {
        horizontal: false,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val?.toFixed(0)} %`,
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
      background: {
        enabled: true,
        foreColor: '#000',
        padding: 6,
        borderColor: '#000',
        borderWidth: 1,
        opacity: 1,
      },
    },
    stroke: {
      show: true,
      width: 1,
      colors: ['#fff'],
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'regular',
      labels: {
        colors: ['#000'],
        useSeriesColors: true,
      },
      position: 'bottom',
      offsetY: 10,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${val?.toFixed(0)} %`,
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => `${val?.toFixed(0)} %`,
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
    xaxis: {
      categories: [''],
      labels: {
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
  })

  watch(
    () => [reportStore.getBaseChartColors, reportStore.getCompareChartColors],
    ([baseColor, compareColor]) => {
      chartOptionsPercent.value = {
        ...chartOptionsPercent.value,
        colors: [baseColor, '#353a45', compareColor],
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getAmountEnergySensorUtilizationPercentage,
    (newData) => {
      if (newData) {
        seriesPercentage.value[0].data = [newData.totalUtilizationPercentage]
        seriesPercentage.value[1].data = [newData.metaPercentage]
        seriesPercentage.value[2].data = [sensorCompareData.totalUtilizationPercentage]
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getCompareAmountEnergySensorUtilizationPercentage,
    (newData) => {
      if (newData) {
        seriesPercentage.value[0].data = [sensorData.totalUtilizationPercentage]
        seriesPercentage.value[1].data = [sensorData.metaPercentage]
        seriesPercentage.value[2].data = [newData.totalUtilizationPercentage]
      } else {
        seriesPercentage.value[2].data = []
      }
    },
    { immediate: true, deep: true },
  )

  return { chartOptionsPercent, seriesPercentage }
}
